import { useState, useMemo, useEffect, useCallback } from 'react';
import { format, addDays, addYears, addMonths, addWeeks, startOfWeek, endOfWeek, startOfMonth, endOfMonth, eachDayOfInterval, isSameDay, isToday, subMonths, subWeeks, getMonth, getDate } from 'date-fns';
import { tr } from 'date-fns/locale';
import { Layout } from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '@/components/ui/select';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogTrigger 
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { 
  Calendar as CalendarIcon, 
  ChevronLeft, 
  ChevronRight, 
  Plus, 
  MapPin, 
  Gift, 
  Heart, 
  CreditCard, 
  Phone, 
  Filter,
  Check,
  CheckCircle2,
  Circle,
  Clock
} from 'lucide-react';
import { cn, formatTurkishPhoneForCall } from '@/lib/utils';
import { useToast } from '@/hooks/use-toast';
import { useCalendar, type CalendarEvent } from '@/hooks/use-calendar';
import { useCompany } from '@/hooks/use-company';
import { useLocations } from '@/hooks/use-locations';


type ViewType = 'day' | 'week' | 'month';

export default function Calendar() {
  // Define event configuration inside component to avoid hoisting issues
  const eventConfig = useMemo(() => ({
    birthday: { 
      icon: Gift, 
      color: 'bg-pink-500/20 text-pink-600 border-pink-300',
      bgColor: 'bg-pink-50 dark:bg-pink-950'
    },
    anniversary: { 
      icon: Heart, 
      color: 'bg-red-500/20 text-red-600 border-red-300',
      bgColor: 'bg-red-50 dark:bg-red-950'
    },
    payment: { 
      icon: CreditCard, 
      color: 'bg-green-500/20 text-green-600 border-green-300',
      bgColor: 'bg-green-50 dark:bg-green-950'
    },
    'follow-up': { 
      icon: Phone, 
      color: 'bg-blue-500/20 text-blue-600 border-blue-300',
      bgColor: 'bg-blue-50 dark:bg-blue-950'
    },
    appointment: { 
      icon: CalendarIcon, 
      color: 'bg-purple-500/20 text-purple-600 border-purple-300',
      bgColor: 'bg-purple-50 dark:bg-purple-950'
    },
    meeting: { 
      icon: Phone, 
      color: 'bg-orange-500/20 text-orange-600 border-orange-300',
      bgColor: 'bg-orange-50 dark:bg-orange-950'
    },
    task: { 
      icon: CalendarIcon, 
      color: 'bg-gray-500/20 text-gray-600 border-gray-300',
      bgColor: 'bg-gray-50 dark:bg-gray-950'
    },
    visit_reminder: { 
      icon: MapPin, 
      color: 'bg-amber-500/20 text-amber-600 border-amber-300',
      bgColor: 'bg-amber-50 dark:bg-amber-950'
    }
  }), []);

  const { toast } = useToast();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewType, setViewType] = useState<ViewType>('month');
  const [selectedLocation, setSelectedLocation] = useState('all');
  const [showAddEvent, setShowAddEvent] = useState(false);
  const [draggedEvent, setDraggedEvent] = useState<CalendarEvent | null>(null);
  const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(null);
  const [showEventDetails, setShowEventDetails] = useState(false);
  const [delayDate, setDelayDate] = useState<Date | undefined>(undefined);
  const [showDelayPopover, setShowDelayPopover] = useState(false);

  // Hooks
  const { 
    events, 
    loading, 
    createEvent,
    updateEventData,
    generateCustomerRecurringEvents,
    isCreating,
    isUpdating
  } = useCalendar();
  
  const { currentCompany } = useCompany();
  const { locations, getActiveLocations } = useLocations();

  // Get available locations with "All Locations" option
  const availableLocations = useMemo(() => {
    const activeLocations = getActiveLocations();
    return [
      { value: 'all', label: 'Tüm Lokasyonlar' },
      ...activeLocations.map(loc => ({ value: loc.id, label: loc.name }))
    ];
  }, [locations, getActiveLocations]);

  // Define which event types can be completed
  const completableEventTypes = ['visit_reminder', 'follow-up', 'payment', 'task', 'appointment', 'meeting'];
  
  // Check if an event can be completed
  const isCompletableEvent = (event: CalendarEvent) => {
    return completableEventTypes.includes(event.event_type);
  };

  // Check if an event is completed
  const isEventCompleted = (event: CalendarEvent) => {
    return event.status === 'completed' || event.completion_percentage === 100;
  };

  // Helper function to get customer details from event
  const getCustomerDetails = (event: CalendarEvent) => {
    // First priority: direct customer relationship
    if (event.customer) {
      return event.customer;
    }
    
    // Second priority: customer from visit relationship
    if (event.visit?.customer) {
      return event.visit.customer;
    }
    
    // Third priority: customer name from metadata
    if ((event.metadata as any)?.customer_name) {
      return {
        first_name: (event.metadata as any).customer_name,
        last_name: null,
        phone: null,
        email: null,
      };
    }
    
    return null;
  };

  // Helper function to format customer name
  const formatCustomerName = (customer: any) => {
    if (!customer) return null;
    return `${customer.first_name || ''} ${customer.last_name || ''}`.trim() || 'İsimsiz Müşteri';
  };

  // Toggle event completion
  const toggleEventCompletion = useCallback(async (event: CalendarEvent) => {
    const isCompleted = isEventCompleted(event);
    const newStatus = isCompleted ? 'scheduled' : 'completed';
    const newCompletionPercentage = isCompleted ? 0 : 100;
    
    try {
      updateEventData({
        eventId: event.id,
        updates: {
          status: newStatus,
          completion_percentage: newCompletionPercentage,
          updated_at: new Date().toISOString()
        }
      });
      
      toast({
        title: isCompleted ? "Hatırlatıcı Tekrar Açıldı" : "Hatırlatıcı Tamamlandı",
        description: `${event.title} ${isCompleted ? 'tekrar açıldı' : 'tamamlandı olarak işaretlendi'}`,
      });
    } catch (error) {
      console.error('Error toggling event completion:', error);
      toast({
        title: "Hata",
        description: "Hatırlatıcı durumu güncellenirken bir hata oluştu.",
        variant: "destructive",
      });
    }
  }, [updateEventData, toast]);

  // Delay event
  const delayEvent = useCallback(async (event: CalendarEvent, newDate: Date) => {
    try {
      updateEventData({
        eventId: event.id,
        updates: {
          start_date: format(newDate, 'yyyy-MM-dd'),
          updated_at: new Date().toISOString()
        }
      });
      
      toast({
        title: "Hatırlatıcı Ertelendi",
        description: `${event.title} ${format(newDate, 'dd.MM.yyyy')} tarihine ertelendi`,
      });
      
      setShowDelayPopover(false);
      setDelayDate(undefined);
      setShowEventDetails(false);
    } catch (error) {
      console.error('Error delaying event:', error);
      toast({
        title: "Hata",
        description: "Hatırlatıcı ertelenirken bir hata oluştu.",
        variant: "destructive",
      });
    }
  }, [updateEventData, toast]);

  const [newEvent, setNewEvent] = useState({
    title: '',
    type: 'follow-up' as CalendarEvent['event_type'],
    date: new Date(),
    time: '',
    location: '',
    description: '',
    customerName: '',
    visitId: '',
    isRecurring: false,
    recurrencePattern: 'monthly' as const,
    recurrenceInterval: 1
  });

  // Memoize the generateCustomerRecurringEvents function
  const handleGenerateCustomerEvents = useCallback(() => {
    if (currentCompany?.id) {
      generateCustomerRecurringEvents();
    }
  }, [currentCompany?.id, generateCustomerRecurringEvents]);

  // Load events on component mount and generate customer recurring events
  useEffect(() => {
    handleGenerateCustomerEvents();
  }, [handleGenerateCustomerEvents]);

  // Set default location when locations are loaded
  useEffect(() => {
    if (availableLocations.length > 1 && !newEvent.location) {
      setNewEvent(prev => ({ ...prev, location: availableLocations[1].value }));
    }
  }, [availableLocations, newEvent.location]);

  const filteredEvents = useMemo(() => {
    if (selectedLocation === 'all') {
      return events;
    }
    return events.filter(event => event.location_id === selectedLocation);
  }, [events, selectedLocation]);

  // Helper function to check if a recurring event should appear on a given date
  const isEventOnDate = (event: CalendarEvent, date: Date): boolean => {
    // Normalize dates to start of day for comparison
    const eventStartDate = new Date(event.start_date);
    eventStartDate.setHours(0, 0, 0, 0);
    const normalizedDate = new Date(date);
    normalizedDate.setHours(0, 0, 0, 0);
    
    // If event is not recurring, check exact date match
    if (!event.is_recurring) {
      return isSameDay(eventStartDate, normalizedDate);
    }
    
    // Handle recurring events
    if (event.recurrence_pattern === 'yearly') {
      // For yearly events, check if month and day match
      const eventMonth = getMonth(eventStartDate);
      const eventDay = getDate(eventStartDate);
      const dateMonth = getMonth(normalizedDate);
      const dateDay = getDate(normalizedDate);
      
      // Check if month and day match
      if (eventMonth === dateMonth && eventDay === dateDay) {
        // Check if date is after or equal to the event start date
        if (normalizedDate >= eventStartDate) {
          // Check recurrence_end_date if it exists
          if (event.recurrence_end_date) {
            const endDate = new Date(event.recurrence_end_date);
            endDate.setHours(0, 0, 0, 0);
            return normalizedDate <= endDate;
          }
          return true;
        }
      }
      return false;
    } else if (event.recurrence_pattern === 'monthly') {
      // For monthly events, check if day of month matches
      const eventDay = getDate(eventStartDate);
      const dateDay = getDate(normalizedDate);
      
      if (eventDay === dateDay && normalizedDate >= eventStartDate) {
        if (event.recurrence_end_date) {
          const endDate = new Date(event.recurrence_end_date);
          endDate.setHours(0, 0, 0, 0);
          return normalizedDate <= endDate;
        }
        return true;
      }
      return false;
    } else if (event.recurrence_pattern === 'weekly') {
      // For weekly events, check if day of week matches
      const eventDayOfWeek = eventStartDate.getDay();
      const dateDayOfWeek = normalizedDate.getDay();
      
      if (eventDayOfWeek === dateDayOfWeek && normalizedDate >= eventStartDate) {
        if (event.recurrence_end_date) {
          const endDate = new Date(event.recurrence_end_date);
          endDate.setHours(0, 0, 0, 0);
          return normalizedDate <= endDate;
        }
        return true;
      }
      return false;
    } else if (event.recurrence_pattern === 'daily') {
      // For daily events, check if date is after start date
      if (normalizedDate >= eventStartDate) {
        if (event.recurrence_end_date) {
          const endDate = new Date(event.recurrence_end_date);
          endDate.setHours(0, 0, 0, 0);
          return normalizedDate <= endDate;
        }
        return true;
      }
      return false;
    }
    
    // Fallback to exact date match for unknown patterns
    return isSameDay(eventStartDate, normalizedDate);
  };

  const getEventsForDate = (date: Date) => {
    return filteredEvents.filter(event => isEventOnDate(event, date));
  };

  const navigateDate = useCallback((direction: 'prev' | 'next') => {
    if (viewType === 'month') {
      setCurrentDate(direction === 'prev' ? subMonths(currentDate, 1) : addMonths(currentDate, 1));
    } else if (viewType === 'week') {
      setCurrentDate(direction === 'prev' ? subWeeks(currentDate, 1) : addWeeks(currentDate, 1));
    } else {
      setCurrentDate(direction === 'prev' ? addDays(currentDate, -1) : addDays(currentDate, 1));
    }
  }, [viewType, currentDate]);

  const handleAddEvent = useCallback(async () => {
    try {
      createEvent({
        title: newEvent.title,
        event_type: newEvent.type,
        start_date: format(newEvent.date, 'yyyy-MM-dd'),
        start_time: newEvent.time || undefined,
        location_id: newEvent.location || undefined,
        description: newEvent.description || undefined,
        is_all_day: !newEvent.time,
        is_recurring: newEvent.isRecurring,
        recurrence_pattern: newEvent.isRecurring ? newEvent.recurrencePattern : undefined,
        recurrence_interval: newEvent.isRecurring ? newEvent.recurrenceInterval : undefined,
        visit_id: newEvent.visitId || undefined,
        metadata: {
          ...(newEvent.customerName ? { customer_name: newEvent.customerName } : {}),
          ...(newEvent.visitId ? { reminder_for_visit: newEvent.visitId } : {})
        }
      });

      setShowAddEvent(false);
      setNewEvent({
        title: '',
        type: 'follow-up',
        date: new Date(),
        time: '',
        location: '',
        description: '',
        customerName: '',
        visitId: '',
        isRecurring: false,
        recurrencePattern: 'monthly',
        recurrenceInterval: 1
      });
    } catch (error) {
      console.error('Error creating event:', error);
    }
  }, [newEvent, createEvent]);

  const handleDragStart = useCallback((event: CalendarEvent) => {
    setDraggedEvent(event);
  }, []);

  const handleEventClick = useCallback((event: CalendarEvent, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedEvent(event);
    setShowEventDetails(true);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
  }, []);

  const handleDrop = useCallback(async (e: React.DragEvent, targetDate: Date) => {
    e.preventDefault();
    if (draggedEvent) {
      try {
        updateEventData({ 
          eventId: draggedEvent.id, 
          updates: { start_date: format(targetDate, 'yyyy-MM-dd') } 
        });
        
        toast({
          title: "Etkinlik Taşındı",
          description: `${draggedEvent.title} ${format(targetDate, 'd MMM yyyy', { locale: tr })} tarihine taşındı`,
        });
      } catch (error) {
        console.error('Error updating event:', error);
        toast({
          title: "Hata",
          description: "Etkinlik taşınırken bir hata oluştu.",
          variant: "destructive",
        });
      }
      
      setDraggedEvent(null);
    }
  }, [draggedEvent, updateEventData, toast]);

  const renderMonthView = useCallback(() => {
    const monthStart = startOfMonth(currentDate);
    const monthEnd = endOfMonth(currentDate);
    const calendarStart = startOfWeek(monthStart);
    const calendarEnd = endOfWeek(monthEnd);
    const days = eachDayOfInterval({ start: calendarStart, end: calendarEnd });

    return (
      <div className="grid grid-cols-7 gap-1">
        {/* Week headers */}
        {['Paz', 'Pzt', 'Sal', 'Çar', 'Per', 'Cum', 'Cmt'].map(day => (
          <div key={day} className="p-2 text-center text-sm font-medium text-muted-foreground">
            {day}
          </div>
        ))}
        
        {/* Calendar days */}
        {days.map(day => {
          const dayEvents = getEventsForDate(day);
          const isCurrentMonth = day.getMonth() === currentDate.getMonth();
          
          return (
            <div
              key={day.toISOString()}
              className={cn(
                "min-h-24 p-2 border border-white/20 transition-all duration-300 rounded-lg",
                isCurrentMonth 
                  ? "bg-white/20 dark:bg-gray-800/20 backdrop-blur-sm" 
                  : "bg-gray-100/30 dark:bg-gray-900/30",
                isToday(day) && "bg-gradient-to-br from-blue-500/20 to-purple-500/20 border-blue-300/50 shadow-lg"
              )}
              onDragOver={handleDragOver}
              onDrop={(e) => handleDrop(e, day)}
            >
              <div className={cn(
                "text-sm font-medium mb-1",
                isCurrentMonth ? "text-foreground" : "text-muted-foreground",
                isToday(day) && "text-primary font-bold"
              )}>
                {format(day, 'd')}
              </div>
              
              <div className="space-y-1">
                {dayEvents.slice(0, 2).map(event => {
                  const config = eventConfig[event.event_type as keyof typeof eventConfig] || eventConfig['task'];
                  const Icon = config.icon;
                  const isCompleted = isEventCompleted(event);
                  const isCompletable = isCompletableEvent(event);
                  
                  return (
                    <div
                      key={event.id}
                      draggable
                      onDragStart={() => handleDragStart(event)}
                      className={cn(
                        "p-2 rounded-lg text-xs cursor-pointer border border-white/30 flex items-center gap-1 group backdrop-blur-sm shadow-sm",
                        "bg-gradient-to-r from-white/60 to-white/40 dark:from-gray-800/60 dark:to-gray-700/40",
                        config.color,
                        isCompleted && "opacity-60 line-through",
                        "hover:shadow-md hover:scale-105 transition-all duration-200"
                      )}
                      title={event.description || event.title}
                    >
                      <Icon className="w-3 h-3 flex-shrink-0" />
                      <span 
                        className="truncate flex-1"
                        onClick={(e) => handleEventClick(event, e)}
                      >
                        {event.title}
                      </span>
                      {isCompletable && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleEventCompletion(event);
                          }}
                          className="opacity-0 group-hover:opacity-100 transition-opacity"
                          disabled={isUpdating}
                          title={isCompleted ? "Tekrar aç" : "Tamamlandı olarak işaretle"}
                        >
                          {isCompleted ? (
                            <CheckCircle2 className="w-3 h-3 text-green-600" />
                          ) : (
                            <Circle className="w-3 h-3 text-gray-400 hover:text-green-600" />
                          )}
                        </button>
                      )}
                    </div>
                  );
                })}
                
                {dayEvents.length > 2 && (
                  <div className="text-xs text-muted-foreground">
                    +{dayEvents.length - 2} more
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    );
  }, [currentDate, eventConfig, filteredEvents, handleDragOver, handleDragStart, handleDrop]);

  const renderWeekView = useCallback(() => {
    const weekStart = startOfWeek(currentDate);
    const weekDays = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));

    return (
      <div className="grid grid-cols-7 gap-1">
        {weekDays.map(day => {
          const dayEvents = getEventsForDate(day);
          
          return (
            <div key={day.toISOString()} className="border border-border rounded-lg p-3">
              <div className={cn(
                "text-center mb-3",
                isToday(day) && "text-primary font-bold"
              )}>
                <div className="text-sm text-muted-foreground">
                  {format(day, 'EEE', { locale: tr })}
                </div>
                <div className={cn(
                  "text-lg font-semibold",
                  isToday(day) && "bg-primary text-primary-foreground rounded-full w-8 h-8 flex items-center justify-center mx-auto"
                )}>
                  {format(day, 'd')}
                </div>
              </div>
              
              <div 
                className="space-y-2 min-h-32"
                onDragOver={handleDragOver}
                onDrop={(e) => handleDrop(e, day)}
              >
                {dayEvents.map(event => {
                  const config = eventConfig[event.event_type as keyof typeof eventConfig] || eventConfig['task'];
                  const Icon = config.icon;
                  const isCompleted = isEventCompleted(event);
                  const isCompletable = isCompletableEvent(event);
                  
                  return (
                    <div
                      key={event.id}
                      draggable
                      onDragStart={() => handleDragStart(event)}
                      className={cn(
                        "p-3 rounded-xl border border-white/30 cursor-pointer group backdrop-blur-sm shadow-lg",
                        "bg-gradient-to-br from-white/60 via-white/40 to-white/20 dark:from-gray-800/60 dark:via-gray-700/40 dark:to-gray-800/20",
                        config.bgColor,
                        isCompleted && "opacity-60",
                        "hover:shadow-xl hover:scale-102 transition-all duration-300"
                      )}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <Icon className="w-4 h-4" />
                        <span 
                          className={cn(
                            "text-sm font-medium flex-1",
                            isCompleted && "line-through"
                          )}
                          onClick={(e) => handleEventClick(event, e)}
                        >
                          {event.title}
                        </span>
                        {isCompletable && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleEventCompletion(event);
                            }}
                            className="opacity-0 group-hover:opacity-100 transition-opacity"
                            disabled={isUpdating}
                            title={isCompleted ? "Tekrar aç" : "Tamamlandı olarak işaretle"}
                          >
                            {isCompleted ? (
                              <CheckCircle2 className="w-4 h-4 text-green-600" />
                            ) : (
                              <Circle className="w-4 h-4 text-gray-400 hover:text-green-600" />
                            )}
                          </button>
                        )}
                      </div>
                      {event.start_time && (
                        <div className="text-xs text-muted-foreground">{event.start_time}</div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    );
  }, [currentDate, eventConfig, filteredEvents, handleDragOver, handleDrop]);

  const renderDayView = useCallback(() => {
    const dayEvents = getEventsForDate(currentDate);
    const timeSlots = Array.from({ length: 24 }, (_, i) => i);

    return (
      <div className="space-y-4">
        <div className="text-center">
          <h2 className="text-2xl font-bold">
            {format(currentDate, 'EEEE, MMMM d, yyyy', { locale: tr })}
          </h2>
        </div>
        
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-white/60 via-white/40 to-white/20 dark:from-gray-800/60 dark:via-gray-700/40 dark:to-gray-800/20 backdrop-blur-lg border border-white/30 shadow-xl">
          <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 via-purple-500/5 to-pink-500/5" />
          <div className="relative p-8">
            {dayEvents.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <div className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-blue-500/20 to-purple-600/20 backdrop-blur-sm border border-white/30 flex items-center justify-center shadow-lg">
                  <CalendarIcon className="w-10 h-10 text-blue-600 dark:text-blue-400" />
                </div>
                <h3 className="text-xl font-semibold mb-3 bg-gradient-to-r from-gray-900 to-gray-600 dark:from-white dark:to-gray-300 bg-clip-text text-transparent">Planlanmış etkinlik yok</h3>
                <p className="text-gray-600 dark:text-gray-400">Başlamak için bir etkinlik ekleyin</p>
              </div>
            ) : (
              <div className="space-y-3">
                {dayEvents.map(event => {
                  const config = eventConfig[event.event_type as keyof typeof eventConfig] || eventConfig['task'];
                  const Icon = config.icon;
                  const isCompleted = isEventCompleted(event);
                  const isCompletable = isCompletableEvent(event);
                  
                  return (
                    <div
                      key={event.id}
                      draggable
                      onDragStart={() => handleDragStart(event)}
                      className={cn(
                        "p-5 rounded-2xl border border-white/30 cursor-pointer group backdrop-blur-lg shadow-xl",
                        "bg-gradient-to-br from-white/70 via-white/50 to-white/30 dark:from-gray-800/70 dark:via-gray-700/50 dark:to-gray-800/30",
                        config.bgColor,
                        isCompleted && "opacity-60",
                        "hover:shadow-2xl hover:scale-105 transition-all duration-300"
                      )}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-3 flex-1">
                          <Icon className="w-5 h-5" />
                          <div 
                            className="flex-1"
                            onClick={(e) => handleEventClick(event, e)}
                          >
                            <h4 className={cn(
                              "font-medium",
                              isCompleted && "line-through"
                            )}>
                              {event.title}
                            </h4>
                            {event.start_time && (
                              <p className="text-sm text-muted-foreground">{event.start_time}</p>
                            )}
                            {getCustomerDetails(event) && (
                              <p className="text-sm text-muted-foreground">
                                Müşteri: {formatCustomerName(getCustomerDetails(event))}
                              </p>
                            )}
                            {event.event_type === 'visit_reminder' && event.visit && (
                              <p className="text-sm text-muted-foreground">
                                Ziyaret: {format(new Date(event.visit.started_at), 'dd.MM.yyyy HH:mm', { locale: tr })}
                              </p>
                            )}
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-2">
                          {isCompletable && (
                            <Button
                              variant={isCompleted ? "default" : "outline"}
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleEventCompletion(event);
                              }}
                              disabled={isUpdating}
                              className={cn(
                                "opacity-0 group-hover:opacity-100 transition-opacity gap-1",
                                isCompleted ? "bg-green-600 hover:bg-green-700" : "hover:bg-green-50"
                              )}
                            >
                              {isCompleted ? (
                                <>
                                  <CheckCircle2 className="w-4 h-4" />
                                  Tamamlandı
                                </>
                              ) : (
                                <>
                                  <Circle className="w-4 h-4" />
                                  Tamamla
                                </>
                              )}
                            </Button>
                          )}
                          <Badge variant="outline" className={config.color}>
                            {event.event_type}
                          </Badge>
                          {event.location_id && (
                            <div className="flex items-center gap-1 text-muted-foreground">
                              <MapPin className="w-3 h-3" />
                              <span className="text-xs">
                                {availableLocations.find(loc => loc.value === event.location_id)?.label || 'Bilinmeyen Lokasyon'}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                      
                      {event.description && (
                        <p className="mt-2 text-sm text-muted-foreground">{event.description}</p>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }, [currentDate, eventConfig, filteredEvents, availableLocations]);

  const getDateRangeText = useCallback(() => {
    if (viewType === 'month') {
      return format(currentDate, 'MMMM yyyy', { locale: tr });
    } else if (viewType === 'week') {
      const weekStart = startOfWeek(currentDate);
      const weekEnd = endOfWeek(currentDate);
      return `${format(weekStart, 'MMM d', { locale: tr })} - ${format(weekEnd, 'MMM d, yyyy', { locale: tr })}`;
    } else {
      return format(currentDate, 'MMMM d, yyyy', { locale: tr });
    }
  }, [viewType, currentDate]);

  return (
    <Layout>
      <div className="space-y-8">
        {/* Glassmorphic Header */}
        <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-white/40 via-white/20 to-white/10 dark:from-gray-900/40 dark:via-gray-800/20 dark:to-gray-900/10 backdrop-blur-xl border border-white/20 shadow-2xl">
          <div className="absolute inset-0 bg-gradient-to-br from-blue-500/10 via-purple-500/10 to-pink-500/10" />
          <div className="relative p-8">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-500/20 to-purple-600/20 backdrop-blur-sm border border-white/30 flex items-center justify-center shadow-lg">
                  <CalendarIcon className="w-8 h-8 text-blue-600 dark:text-blue-400" />
                </div>
                <div>
                  <h1 className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-gray-900 to-gray-600 dark:from-white dark:to-gray-300 bg-clip-text text-transparent">
                    Takvim
                  </h1>
                  <p className="text-lg text-gray-600 dark:text-gray-400 mt-1">Etkinlik ve takipleri yönet</p>
                </div>
              </div>
              
              <Dialog open={showAddEvent} onOpenChange={setShowAddEvent}>
                <DialogTrigger asChild>
                  <Button className="gap-3 px-6 py-3 text-white bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 border-0 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 backdrop-blur-sm">
                    <Plus className="w-5 h-5" />
                    Etkinlik Ekle
                  </Button>
                </DialogTrigger>
            <DialogContent className="sm:max-w-md bg-white/95 dark:bg-gray-900/95 backdrop-blur-xl border border-white/20 shadow-2xl rounded-2xl">
              <DialogHeader>
                <DialogTitle>Yeni Etkinlik Ekle</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="title">Başlık</Label>
                  <Input
                    id="title"
                    value={newEvent.title}
                    onChange={(e) => setNewEvent(prev => ({ ...prev, title: e.target.value }))}
                    placeholder="Etkinlik başlığı..."
                  />
                </div>
                
                <div>
                  <Label htmlFor="type">Tür</Label>
                  <Select value={newEvent.type} onValueChange={(value: CalendarEvent['event_type']) => 
                    setNewEvent(prev => ({ ...prev, type: value }))
                  }>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="birthday">🎂 Doğum Günü</SelectItem>
                      <SelectItem value="anniversary">💖 Yıldönümü</SelectItem>
                      <SelectItem value="payment">💳 Ödeme</SelectItem>
                      <SelectItem value="follow-up">📞 Takip</SelectItem>
                      <SelectItem value="appointment">📅 Randevu</SelectItem>
                      <SelectItem value="meeting">🤝 Toplantı</SelectItem>
                      <SelectItem value="task">✅ Görev</SelectItem>
                      <SelectItem value="visit_reminder">🔔 Ziyaret Hatırlatıcısı</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Tarih</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className={cn(
                            "w-full justify-start text-left font-normal",
                            !newEvent.date && "text-muted-foreground"
                          )}
                        >
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {newEvent.date ? format(newEvent.date, "dd MMM yyyy", { locale: tr }) : "Tarih seçin"}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0">
                        <Calendar
                          mode="single"
                          selected={newEvent.date}
                          onSelect={(date) => date && setNewEvent(prev => ({ ...prev, date }))}
                          initialFocus
                          locale={tr}
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                  <div>
                    <Label htmlFor="time">Saat (opsiyonel)</Label>
                    <Input
                      id="time"
                      type="time"
                      value={newEvent.time}
                      onChange={(e) => setNewEvent(prev => ({ ...prev, time: e.target.value }))}
                    />
                  </div>
                </div>
                
                <div>
                  <Label htmlFor="location">Lokasyon</Label>
                  <Select value={newEvent.location} onValueChange={(value) => 
                    setNewEvent(prev => ({ ...prev, location: value }))
                  }>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {availableLocations.slice(1).map(location => (
                        <SelectItem key={location.value} value={location.value}>{location.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label htmlFor="customerName">Müşteri (opsiyonel)</Label>
                  <Input
                    id="customerName"
                    value={newEvent.customerName}
                    onChange={(e) => setNewEvent(prev => ({ ...prev, customerName: e.target.value }))}
                    placeholder="Müşteri adı..."
                  />
                </div>
                
                {newEvent.type === 'visit_reminder' && (
                  <div>
                    <Label htmlFor="visitId">Ziyaret ID (opsiyonel)</Label>
                    <Input
                      id="visitId"
                      value={newEvent.visitId}
                      onChange={(e) => setNewEvent(prev => ({ ...prev, visitId: e.target.value }))}
                      placeholder="Ziyaret ID'si..."
                    />
                  </div>
                )}
                
                <div>
                  <Label htmlFor="description">Açıklama (opsiyonel)</Label>
                  <Textarea
                    id="description"
                    value={newEvent.description}
                    onChange={(e) => setNewEvent(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Etkinlik açıklaması..."
                    rows={3}
                  />
                </div>

                {/* Recurring Event Options */}
                <div className="space-y-4 border-t pt-4">
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id="isRecurring"
                      checked={newEvent.isRecurring}
                      onChange={(e) => setNewEvent(prev => ({ ...prev, isRecurring: e.target.checked }))}
                      className="rounded border-gray-300"
                    />
                    <Label htmlFor="isRecurring" className="text-sm font-medium">
                      Tekrarlanan Etkinlik
                    </Label>
                  </div>

                  {newEvent.isRecurring && (
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="recurrencePattern">Tekrar Düzeni</Label>
                        <Select 
                          value={newEvent.recurrencePattern} 
                          onValueChange={(value: 'daily' | 'weekly' | 'monthly' | 'yearly') => 
                            setNewEvent(prev => ({ ...prev, recurrencePattern: value }))
                          }
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="daily">Günlük</SelectItem>
                            <SelectItem value="weekly">Haftalık</SelectItem>
                            <SelectItem value="monthly">Aylık</SelectItem>
                            <SelectItem value="yearly">Yıllık</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label htmlFor="recurrenceInterval">Her</Label>
                        <Input
                          id="recurrenceInterval"
                          type="number"
                          min="1"
                          max="365"
                          value={newEvent.recurrenceInterval}
                          onChange={(e) => setNewEvent(prev => ({ ...prev, recurrenceInterval: parseInt(e.target.value) || 1 }))}
                          placeholder="1"
                        />
                      </div>
                    </div>
                  )}
                </div>
                
                <div className="flex gap-2">
                  <Button variant="outline" className="flex-1" onClick={() => setShowAddEvent(false)}>
                    İptal
                  </Button>
                  <Button className="flex-1" onClick={handleAddEvent} disabled={!newEvent.title || isCreating}>
                    {isCreating ? 'Ekleniyor...' : 'Etkinlik Ekle'}
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
            </div>
          </div>
        </div>

        {/* Event Details Dialog */}
        <Dialog open={showEventDetails} onOpenChange={setShowEventDetails}>
          <DialogContent className="sm:max-w-lg bg-white/95 dark:bg-gray-900/95 backdrop-blur-xl border border-white/20 shadow-2xl rounded-2xl">
            <DialogHeader>
              <DialogTitle>Etkinlik Detayları</DialogTitle>
            </DialogHeader>
            {selectedEvent && (
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  {(() => {
                    const config = eventConfig[selectedEvent.event_type as keyof typeof eventConfig] || eventConfig['task'];
                    const Icon = config.icon;
                    return <Icon className="w-6 h-6" />;
                  })()}
                  <div>
                    <h3 className="text-lg font-semibold">{selectedEvent.title}</h3>
                    <div className="flex items-center gap-2 mt-1">
                      <Badge variant="outline" className={
                        eventConfig[selectedEvent.event_type as keyof typeof eventConfig]?.color || eventConfig['task'].color
                      }>
                        {{
                          'birthday': 'Doğum Günü',
                          'anniversary': 'Yıldönümü',
                          'payment': 'Ödeme',
                          'follow-up': 'Takip',
                          'appointment': 'Randevu',
                          'meeting': 'Toplantı',
                          'task': 'Görev',
                          'visit_reminder': 'Ziyaret Hatırlatıcısı'
                        }[selectedEvent.event_type] || selectedEvent.event_type}
                      </Badge>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm font-medium text-muted-foreground">Tarih</Label>
                    <p className="text-sm">{format(new Date(selectedEvent.start_date), 'dd MMMM yyyy', { locale: tr })}</p>
                  </div>
                  {selectedEvent.start_time && (
                    <div>
                      <Label className="text-sm font-medium text-muted-foreground">Saat</Label>
                      <p className="text-sm">{selectedEvent.start_time}</p>
                    </div>
                  )}
                </div>

                {selectedEvent.location_id && (
                  <div>
                    <Label className="text-sm font-medium text-muted-foreground">Lokasyon</Label>
                    <p className="text-sm flex items-center gap-1">
                      <MapPin className="w-3 h-3" />
                      {availableLocations.find(loc => loc.value === selectedEvent.location_id)?.label || 'Bilinmeyen Lokasyon'}
                    </p>
                  </div>
                )}

                {getCustomerDetails(selectedEvent) && (
                  <div className="space-y-3">
                    <div>
                      <Label className="text-sm font-medium text-muted-foreground">Müşteri Bilgileri</Label>
                      <div className="bg-gradient-to-br from-blue-50/50 to-purple-50/50 dark:from-blue-950/30 dark:to-purple-950/30 backdrop-blur-sm p-4 rounded-xl border border-white/20 space-y-2">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium">İsim:</span>
                          <span className="text-sm">{formatCustomerName(getCustomerDetails(selectedEvent))}</span>
                        </div>
                        {getCustomerDetails(selectedEvent)?.phone && (
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium">Telefon:</span>
                            <a 
                              href={`tel:${formatTurkishPhoneForCall(getCustomerDetails(selectedEvent)?.phone || '')}`}
                              className="text-sm text-blue-600 hover:text-blue-800 underline"
                            >
                              {getCustomerDetails(selectedEvent)?.phone}
                            </a>
                          </div>
                        )}
                        {getCustomerDetails(selectedEvent)?.email && (
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium">E-posta:</span>
                            <a 
                              href={`mailto:${getCustomerDetails(selectedEvent)?.email}`}
                              className="text-sm text-blue-600 hover:text-blue-800 underline"
                            >
                              {getCustomerDetails(selectedEvent)?.email}
                            </a>
                          </div>
                        )}
                        {selectedEvent.customer?.visit_count_total !== undefined && (
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium">Toplam Ziyaret:</span>
                            <span className="text-sm">{selectedEvent.customer.visit_count_total}</span>
                          </div>
                        )}
                        {selectedEvent.customer?.last_seen_at && (
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium">Son Görülme:</span>
                            <span className="text-sm">{format(new Date(selectedEvent.customer.last_seen_at), 'dd.MM.yyyy HH:mm', { locale: tr })}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {selectedEvent.event_type === 'visit_reminder' && selectedEvent.visit && (
                  <div className="space-y-2">
                    <Label className="text-sm font-medium text-muted-foreground">Ziyaret Detayları</Label>
                    <div className="bg-gradient-to-br from-amber-50/50 to-orange-50/50 dark:from-amber-950/30 dark:to-orange-950/30 backdrop-blur-sm p-4 rounded-xl border border-white/20 space-y-2">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium">Ziyaret Tarihi:</span>
                        <span className="text-sm">{format(new Date(selectedEvent.visit.started_at), 'dd.MM.yyyy HH:mm', { locale: tr })}</span>
                      </div>
                      {selectedEvent.visit.ended_at && (
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium">Bitiş Tarihi:</span>
                          <span className="text-sm">{format(new Date(selectedEvent.visit.ended_at), 'dd.MM.yyyy HH:mm', { locale: tr })}</span>
                        </div>
                      )}
                      {selectedEvent.visit.duration_sec && (
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium">Süre:</span>
                          <span className="text-sm">{Math.round(selectedEvent.visit.duration_sec / 60)} dakika</span>
                        </div>
                      )}
                      {selectedEvent.visit.notes && (
                        <div className="space-y-1">
                          <span className="text-sm font-medium">Ziyaret Notları:</span>
                          <p className="text-sm bg-white dark:bg-gray-900 p-2 rounded border">
                            {selectedEvent.visit.notes}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {selectedEvent.description && (
                  <div>
                    <Label className="text-sm font-medium text-muted-foreground">Açıklama</Label>
                    <p className="text-sm bg-muted/50 p-3 rounded-md">{selectedEvent.description}</p>
                  </div>
                )}

                {selectedEvent.is_recurring && (
                  <div>
                    <Label className="text-sm font-medium text-muted-foreground">Tekrarlanan Etkinlik</Label>
                    <p className="text-sm">
                      Her {selectedEvent.recurrence_interval} {
                        selectedEvent.recurrence_pattern === 'daily' ? 'gün' :
                        selectedEvent.recurrence_pattern === 'weekly' ? 'hafta' :
                        selectedEvent.recurrence_pattern === 'monthly' ? 'ay' :
                        selectedEvent.recurrence_pattern === 'yearly' ? 'yıl' : selectedEvent.recurrence_pattern
                      }
                    </p>
                  </div>
                )}

                {selectedEvent && (
                  <div className="space-y-3 p-4 bg-gradient-to-r from-green-50/50 to-emerald-50/50 dark:from-green-950/30 dark:to-emerald-950/30 backdrop-blur-sm rounded-xl border border-white/20">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium">Durum:</span>
                        <Badge variant={isEventCompleted(selectedEvent) ? "default" : "outline"} 
                               className={cn(
                                 "backdrop-blur-sm border-white/30",
                                 isEventCompleted(selectedEvent) 
                                   ? "bg-gradient-to-r from-green-600 to-emerald-600 text-white shadow-lg" 
                                   : "bg-white/50 dark:bg-gray-800/50"
                               )}>
                          {isEventCompleted(selectedEvent) ? "Tamamlandı" : "Beklemede"}
                        </Badge>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {/* Tamamlandı Button - Available for all reminders */}
                      <Button
                        variant={isEventCompleted(selectedEvent) ? "outline" : "default"}
                        size="sm"
                        onClick={() => toggleEventCompletion(selectedEvent)}
                        disabled={isUpdating}
                        className={cn(
                          "gap-2 backdrop-blur-sm border-white/30 rounded-xl transition-all duration-300 flex-1",
                          isEventCompleted(selectedEvent) 
                            ? "bg-white/50 dark:bg-gray-800/50 hover:bg-red-100/50 dark:hover:bg-red-900/50" 
                            : "bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white shadow-lg"
                        )}
                      >
                        {isEventCompleted(selectedEvent) ? (
                          <>
                            <Circle className="w-4 h-4" />
                            Tekrar Aç
                          </>
                        ) : (
                          <>
                            <CheckCircle2 className="w-4 h-4" />
                            Tamamlandı
                          </>
                        )}
                      </Button>
                      
                      {/* Ertelendi Button - Available for all non-completed reminders */}
                      {!isEventCompleted(selectedEvent) && (
                        <Popover 
                          open={showDelayPopover} 
                          onOpenChange={(open) => {
                            setShowDelayPopover(open);
                            if (open && selectedEvent) {
                              const currentDate = new Date(selectedEvent.start_date);
                              setDelayDate(addDays(currentDate, 1));
                            } else {
                              setDelayDate(undefined);
                            }
                          }}
                        >
                          <PopoverTrigger asChild>
                            <Button
                              variant="outline"
                              size="sm"
                              className="gap-2 backdrop-blur-sm border-white/30 rounded-xl flex-1"
                            >
                              <Clock className="w-4 h-4" />
                              Ertelendi
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0" align="end">
                            <div className="p-4 space-y-4">
                              <div className="space-y-2">
                                <h4 className="font-medium text-sm">Yeni Tarih Seçin</h4>
                                <Calendar
                                  mode="single"
                                  selected={delayDate}
                                  onSelect={(date) => {
                                    if (date) {
                                      setDelayDate(date);
                                    }
                                  }}
                                  disabled={(date) => {
                                    const today = new Date();
                                    today.setHours(0, 0, 0, 0);
                                    return date < today;
                                  }}
                                  initialFocus
                                />
                              </div>
                              <div className="flex gap-2">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="flex-1"
                                  onClick={() => {
                                    setShowDelayPopover(false);
                                    setDelayDate(undefined);
                                  }}
                                >
                                  İptal
                                </Button>
                                <Button
                                  size="sm"
                                  className="flex-1"
                                  onClick={() => {
                                    if (delayDate && selectedEvent) {
                                      delayEvent(selectedEvent, delayDate);
                                    }
                                  }}
                                  disabled={!delayDate}
                                >
                                  Ertele
                                </Button>
                              </div>
                            </div>
                          </PopoverContent>
                        </Popover>
                      )}
                    </div>
                  </div>
                )}

                <div className="flex justify-end">
                  <Button variant="outline" onClick={() => setShowEventDetails(false)}>
                    Kapat
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Glassmorphic Controls */}
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-white/30 via-white/20 to-white/30 dark:from-gray-900/30 dark:via-gray-800/20 dark:to-gray-900/30 backdrop-blur-lg border border-white/20 shadow-lg">
          <div className="absolute inset-0 bg-gradient-to-r from-blue-500/5 via-purple-500/5 to-pink-500/5" />
          <div className="relative p-6">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <Button variant="outline" size="sm" onClick={() => navigateDate('prev')} className="bg-white/50 dark:bg-gray-800/50 backdrop-blur-sm border-white/30 hover:bg-white/70 dark:hover:bg-gray-700/70 rounded-xl transition-all duration-300">
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <div className="min-w-48 text-center px-4 py-2 bg-gradient-to-r from-white/40 to-white/20 dark:from-gray-800/40 dark:to-gray-700/20 backdrop-blur-sm rounded-xl border border-white/20">
                  <h2 className="text-lg font-semibold bg-gradient-to-r from-gray-900 to-gray-600 dark:from-white dark:to-gray-300 bg-clip-text text-transparent">{getDateRangeText()}</h2>
                </div>
                <Button variant="outline" size="sm" onClick={() => navigateDate('next')} className="bg-white/50 dark:bg-gray-800/50 backdrop-blur-sm border-white/30 hover:bg-white/70 dark:hover:bg-gray-700/70 rounded-xl transition-all duration-300">
                  <ChevronRight className="w-4 h-4" />
                </Button>
                <Button variant="outline" size="sm" onClick={() => setCurrentDate(new Date())} className="bg-white/50 dark:bg-gray-800/50 backdrop-blur-sm border-white/30 hover:bg-white/70 dark:hover:bg-gray-700/70 rounded-xl transition-all duration-300">
                  Bugün
                </Button>
              </div>
              
              <div className="flex items-center gap-3">
                <Select value={selectedLocation} onValueChange={setSelectedLocation}>
                  <SelectTrigger className="w-48 bg-white/50 dark:bg-gray-800/50 backdrop-blur-sm border-white/30 rounded-xl">
                    <Filter className="w-4 h-4 mr-2" />
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-white/95 dark:bg-gray-900/95 backdrop-blur-xl border border-white/20 rounded-xl">
                    {availableLocations.map(location => (
                      <SelectItem key={location.value} value={location.value}>{location.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                
                <div className="flex bg-white/30 dark:bg-gray-800/30 backdrop-blur-sm border border-white/20 rounded-xl overflow-hidden">
                  <Button
                    variant={viewType === 'day' ? 'default' : 'ghost'}
                    size="sm"
                    onClick={() => setViewType('day')}
                    className={cn(
                      "rounded-none border-0",
                      viewType === 'day' 
                        ? "bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-lg" 
                        : "bg-transparent hover:bg-white/50 dark:hover:bg-gray-700/50"
                    )}
                  >
                    Gün
                  </Button>
                  <Button
                    variant={viewType === 'week' ? 'default' : 'ghost'}
                    size="sm"
                    onClick={() => setViewType('week')}
                    className={cn(
                      "rounded-none border-0 border-x border-white/20",
                      viewType === 'week' 
                        ? "bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-lg" 
                        : "bg-transparent hover:bg-white/50 dark:hover:bg-gray-700/50"
                    )}
                  >
                    Hafta
                  </Button>
                  <Button
                    variant={viewType === 'month' ? 'default' : 'ghost'}
                    size="sm"
                    onClick={() => setViewType('month')}
                    className={cn(
                      "rounded-none border-0",
                      viewType === 'month' 
                        ? "bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-lg" 
                        : "bg-transparent hover:bg-white/50 dark:hover:bg-gray-700/50"
                    )}
                  >
                    Ay
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Glassmorphic Calendar Content */}
        <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-white/40 via-white/20 to-white/10 dark:from-gray-900/40 dark:via-gray-800/20 dark:to-gray-900/10 backdrop-blur-xl border border-white/20 shadow-2xl">
          <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 via-purple-500/5 to-pink-500/5" />
          <div className="relative p-8">
            {filteredEvents.length === 0 && selectedLocation === 'all' ? (
              <div className="text-center py-16">
                <div className="w-24 h-24 mx-auto mb-8 rounded-3xl bg-gradient-to-br from-blue-500/20 to-purple-600/20 backdrop-blur-sm border border-white/30 flex items-center justify-center shadow-xl">
                  <CalendarIcon className="w-12 h-12 text-blue-600 dark:text-blue-400" />
                </div>
                <h3 className="text-2xl font-bold mb-4 bg-gradient-to-r from-gray-900 to-gray-600 dark:from-white dark:to-gray-300 bg-clip-text text-transparent">
                  Etkinlik planlanmamış
                </h3>
                <p className="text-lg text-gray-600 dark:text-gray-400 mb-8 max-w-md mx-auto">
                  Düzenli kalmak için doğum günleri, yıldönümleri, ödemeler veya takipler eklemeye başlayın.
                </p>
                <Button 
                  onClick={() => setShowAddEvent(true)}
                  className="gap-3 px-8 py-4 text-white bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 border-0 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 backdrop-blur-sm text-lg"
                >
                  <Plus className="w-5 h-5" />
                  İlk Etkinliğinizi Ekleyin
                </Button>
              </div>
            ) : (
              <div>
                {viewType === 'month' && renderMonthView()}
                {viewType === 'week' && renderWeekView()}
                {viewType === 'day' && renderDayView()}
              </div>
            )}
          </div>
        </div>

        {/* Glassmorphic Legend */}
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-white/30 via-white/20 to-white/30 dark:from-gray-900/30 dark:via-gray-800/20 dark:to-gray-900/30 backdrop-blur-lg border border-white/20 shadow-lg">
          <div className="absolute inset-0 bg-gradient-to-r from-green-500/5 via-teal-500/5 to-blue-500/5" />
          <div className="relative p-6">
            <div className="flex flex-wrap items-center gap-4">
              <span className="text-sm font-medium text-muted-foreground">Etkinlik Türleri:</span>
              {Object.entries(eventConfig).map(([type, config]) => {
                const Icon = config.icon;
                const typeLabels = {
                  'birthday': 'Doğum Günü',
                  'anniversary': 'Yıldönümü',
                  'payment': 'Ödeme',
                  'follow-up': 'Takip',
                  'appointment': 'Randevu',
                  'meeting': 'Toplantı',
                  'task': 'Görev',
                  'visit_reminder': 'Ziyaret Hatırlatıcısı'
                };
                return (
                  <div key={type} className="flex items-center gap-2">
                    <Icon className="w-4 h-4" />
                    <span className="text-sm">{typeLabels[type as keyof typeof typeLabels]}</span>
                  </div>
                );
              })}
              <span className="text-xs text-muted-foreground ml-4">
                💡 Etkinlikleri yeniden planlamak için sürükleyin | ✅ Hatırlatıcıları tamamlamak için üzerine tıklayın
              </span>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}